import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { TruncatedLabel } from '@/components/ui/truncated-label'
import { cn } from '@/lib/utils'
import { Pin, Plus, Search } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useState } from 'react'

const ROW_CLASS = 'flex items-center gap-2 p-(--switcher-item-padding) text-body text-left rounded-sm transition-colors'

export interface SwitcherItem {
	id: string
	name: string
	/** Extra text the search matches, such as a project id or an organization's project names. */
	keywords?: string[]
	selected: boolean
	/** Tinted like a pointer hover, for the row whose projects are on show. */
	hovered?: boolean
	onSelect: () => void
	onHover?: () => void
	onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
	/** Rendered after the pin, such as the chevron that announces a flyout. */
	trailing?: ReactNode
}

interface SwitcherPaneProps {
	/** Test ids derive from it: `-heading`, `-search`, `-empty`, `-row-<id>`, `-item-<id>`, `-pin-<id>`. */
	testId: string
	noun: 'organization' | 'project'
	items: SwitcherItem[]
	heading?: string
	/** Personal pin per row. The button shows on hover and stays once pinned. */
	pin?: { pinnedId: string | null; disabled: boolean; onToggle: (id: string, pinned: boolean) => void }
	/** Rendered under the list, such as a create row. */
	footer?: ReactNode
}

export function SwitcherPane({ testId, noun, items, heading, pin, footer }: SwitcherPaneProps) {
	const [searchTerm, setSearchTerm] = useState('')
	const term = searchTerm.toLowerCase()
	const shown = term
		? items.filter(item => [item.name, ...(item.keywords ?? [])].some(text => text.toLowerCase().includes(term)))
		: items

	return (
		<div className='w-full'>
			{heading && (
				<div
					data-testid={`${testId}-heading`}
					className='flex items-center border-b border-border px-2.5 py-2 text-body font-medium'>
					<TruncatedLabel label={heading} side='bottom' />
				</div>
			)}
			<div className='flex h-(--height-control) items-center gap-2 border-b border-border px-3'>
				<Search className='size-(--icon-standalone) shrink-0 opacity-50' aria-hidden />
				<Input
					data-testid={`${testId}-search`}
					placeholder={`Find ${noun}…`}
					value={searchTerm}
					onChange={e => setSearchTerm(e.target.value)}
					className='rounded-none border-0 bg-transparent p-0 focus-visible:ring-0'
				/>
			</div>
			<div className='space-y-0.5 max-h-48 overflow-y-auto p-1'>
				{shown.length === 0 ? (
					<div data-testid={`${testId}-empty`} className='text-center text-body text-muted-foreground py-6'>
						{term ? `No ${noun}s found` : `No ${noun}s available`}
					</div>
				) : (
					shown.map(item => {
						const pinned = item.id === pin?.pinnedId
						const pinLabel = `${pinned ? 'Unpin default' : 'Set as default'} ${noun}`
						return (
							<div
								key={item.id}
								data-testid={`${testId}-row-${item.id}`}
								className={cn(
									'group/item flex items-center rounded-sm transition-colors',
									item.selected
										? 'bg-accent text-accent-foreground'
										: item.hovered
											? 'bg-accent/50'
											: 'hover:bg-accent/50',
								)}
								onPointerEnter={item.onHover}>
								<button
									data-testid={`${testId}-item-${item.id}`}
									type='button'
									className={cn(ROW_CLASS, 'flex-1 min-w-0 cursor-pointer')}
									onClick={item.onSelect}
									onFocus={item.onHover}
									onKeyDown={item.onKeyDown}>
									<TruncatedLabel label={item.name} side='bottom' className='text-foreground' />
								</button>
								{(pin || item.trailing) && (
									<div className='flex items-center gap-1 pr-2 shrink-0'>
										{pin && (
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														data-testid={`${testId}-pin-${item.id}`}
														type='button'
														aria-label={pinLabel}
														className={cn(
															'p-0.5 rounded-sm transition-opacity cursor-pointer',
															pinned
																? 'opacity-100 text-primary'
																: 'opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100 text-muted-foreground hover:text-foreground',
														)}
														onClick={() => pin.onToggle(item.id, pinned)}
														disabled={pin.disabled}>
														<Pin
															className='size-(--icon-standalone) rotate-45 transform shrink-0'
															fill={pinned ? 'currentColor' : 'none'}
															strokeWidth={2}
														/>
													</button>
												</TooltipTrigger>
												<TooltipContent side='bottom'>{pinLabel}</TooltipContent>
											</Tooltip>
										)}
										{item.trailing}
									</div>
								)}
							</div>
						)
					})
				)}
			</div>
			{footer}
		</div>
	)
}

/** The "New ..." row under a divider. With `disabledReason` it is inert and explains itself on hover. */
export function SwitcherCreateRow({
	testId,
	label,
	onClick,
	disabledReason,
}: {
	testId: string
	label: string
	onClick: () => void
	disabledReason?: ReactNode
}) {
	const row = (
		<button
			data-testid={testId}
			type='button'
			className={cn(
				ROW_CLASS,
				'w-full',
				disabledReason ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:bg-accent/50',
			)}
			onClick={onClick}
			disabled={!!disabledReason}>
			<Plus className='icon-alone' strokeWidth={2} />
			{label}
		</button>
	)
	// The text colour sits on the wrapper: in one class list twMerge would drop `text-body` for it.
	return (
		<div className={cn('border-t border-border p-1', disabledReason ? 'text-muted-foreground' : 'text-foreground')}>
			{disabledReason ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<div className='w-full cursor-not-allowed'>{row}</div>
					</TooltipTrigger>
					<TooltipContent side='right'>{disabledReason}</TooltipContent>
				</Tooltip>
			) : (
				row
			)}
		</div>
	)
}
